import { useRef, useEffect, useCallback } from 'react';

interface PDPMembershipCardStickyProps {
  vehicleImagesColumnRef?: React.RefObject<HTMLDivElement | null>;
  PRISTINE_HERO_PDP?: boolean;
}

export default function usePDPMembershipCardSticky({
  vehicleImagesColumnRef,
  PRISTINE_HERO_PDP = false,
}: PDPMembershipCardStickyProps) {
  const bottomTabRef = useRef<HTMLDivElement>(null);

  // Using refs instead of state to avoid unnecessary re-renders and to allow the event handler closures to still have access to the latest values.
  const lastVehicleImagesColumnBottomRef = useRef<number | null>(null);
  const rafIdRef = useRef<number | null>(null);

  /**
   * Updates the sticky position of the bottom tab.
   * @returns true if the vehicle images column is detected to currently be visibly sticky, false otherwise.
   */
  const updateStickyPosition = useCallback(() => {
    if (
      !vehicleImagesColumnRef?.current ||
      !bottomTabRef.current ||
      typeof window === 'undefined'
    )
      return;

    const vehicleImagesColumnRect =
      vehicleImagesColumnRef.current.getBoundingClientRect();

    // Detect if the vehicle images column is currently sticking to the top of the page
    const isVehicleImagesColumnSticky =
      lastVehicleImagesColumnBottomRef.current !== null &&
      lastVehicleImagesColumnBottomRef.current === vehicleImagesColumnRect.bottom;

    // Only calculate the sticky offset when the vehicle images column is not sticking
    // Need this to prevent an infinite loop of re-renders.
    if (!isVehicleImagesColumnSticky) {
      // Update the last left column bottom value
      lastVehicleImagesColumnBottomRef.current = vehicleImagesColumnRect.bottom;

      // Calculate the sticky offset
      const stickyOffset = Math.max(
        0,
        window.innerHeight - vehicleImagesColumnRect.bottom
      );
      bottomTabRef.current.style.setProperty('--sticky-bottom', `${stickyOffset}px`);
    }

    return isVehicleImagesColumnSticky;
  }, [vehicleImagesColumnRef]);

  const scrollHandler = useCallback(() => {
    if (typeof window === 'undefined') return;

    // If we're still waiting for the last frame to run
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current); // Cancel it
    }

    // Schedule a new update
    rafIdRef.current = requestAnimationFrame(() => {
      const isVehicleImagesColumnSticky = updateStickyPosition();

      // If the left column is sticky, remove the scroll handler as we don't need to recalculate the offset on scroll anymore
      // for this current viewport size.
      if (isVehicleImagesColumnSticky) {
        window.removeEventListener('scroll', scrollHandler);
      }
    });
  }, [updateStickyPosition]);

  const resizeHandler = useCallback(() => {
    if (typeof window === 'undefined') return;

    // On page resize, reset trackers and recalculate sticky position. We need to do this because the sticky offset
    // is dependent on the window height, which can change on resize.
    lastVehicleImagesColumnBottomRef.current = null;

    // Restore on scroll handler
    window.addEventListener('scroll', scrollHandler);

    // If we're still waiting for the last frame to run
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current); // Cancel it
    }

    // Schedule a new update
    rafIdRef.current = requestAnimationFrame(updateStickyPosition);
  }, [scrollHandler, updateStickyPosition]);

  /**
   * On initial mount, update the sticky position of the bottom tab and set up event listeners for scroll and resize events.
   */
  useEffect(() => {
    // Early exit if PRISTINE_HERO_PDP flag is off or during SSR when window is undefined
    if (!PRISTINE_HERO_PDP || typeof window === 'undefined') return;

    // Initial position setup
    updateStickyPosition();

    // Set up event listeners for scroll and resize events
    window.addEventListener('resize', resizeHandler);
    window.addEventListener('scroll', scrollHandler);

    return () => {
      // If we're still waiting for the last frame to run
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current); // Cancel it
      }

      window.removeEventListener('resize', resizeHandler);
      window.removeEventListener('scroll', scrollHandler);
    };
  }, [PRISTINE_HERO_PDP, resizeHandler, scrollHandler, updateStickyPosition]);

  return {
    bottomTabRef,
  };
}
